import React from 'react';
import { Button, Alert, Spinner, Container } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import { LomavisPaidAnalyticsView1 } from '@/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1/LomavisPaidAnalyticsView1';
import type { PaidCampaign } from '@/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1/LomavisPaidAnalyticsView1';
import { LomavisGrowthCardSectionTemplate } from '@/lomavisSharedUi/templates/cards/LomavisGrowthCardSectionTemplate';
import { LomavisBestPerformingAds } from '@/lomavisSharedUi/templates/cards/ILomavisBestPerformingAds';
import {
  buildAdAnalyticsExcelSheetsFromFormatted,
  formatDataset,
  formatUsingAdsNoFilter
} from '../helpers';
import {
  buildBestPerformerCards,
  buildGrowthCardEntries,
  EntityLevel,
  summarizeCampaignInsights
} from '../utils';
import {
  ADS_COMBINED_CONFIG,
  ADS_INSIGHT_CONFIG,
  ADS_STATS_CONFIG,
  UNIT_TYPE_CURRENCY,
  UNIT_TYPE_PERCENT,
  adLevel
} from '../growthConfig';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { getCurrencySymbol, type SupportedCurrency } from '../currency';

interface AdCampaignProps {
  dateRange: { startDate?: string; endDate?: string };
  rawData: PaidCampaign[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  currency: SupportedCurrency;
}

const AdCampaigns: React.FC<AdCampaignProps> = ({
  dateRange,
  rawData,
  loading,
  error,
  onRetry,
  currency
}) => {
  const { t } = useTranslate();
  const [level, setLevel] = React.useState<EntityLevel>(adLevel.ADS);

  // 1) Filter on RAW by date range (intersections)
  const filteredRaw = React.useMemo(
    () => formatUsingAdsNoFilter(rawData as any[]),
    [rawData]
  );
  // 2) Then format for display/export
  const data = React.useMemo(() => formatDataset(filteredRaw), [filteredRaw]);

  const excelSheets = React.useMemo(
    () =>
      buildAdAnalyticsExcelSheetsFromFormatted(data, t, {
        excludeKeys: ['status', 'start_time', 'stop_time']
      }),
    [data, t]
  );

  const campaigns = React.useMemo(
    () => (Array.isArray(filteredRaw) ? filteredRaw : []),
    [filteredRaw]
  );
  const insightConfig = React.useMemo(
    () => (Array.isArray(ADS_INSIGHT_CONFIG) ? ADS_INSIGHT_CONFIG : []),
    []
  );
  const statsConfig = React.useMemo(
    () => (Array.isArray(ADS_STATS_CONFIG) ? ADS_STATS_CONFIG : []),
    []
  );
  const allConfig = React.useMemo(
    () => (Array.isArray(ADS_COMBINED_CONFIG) ? ADS_COMBINED_CONFIG : []),
    []
  );

  const metricUnits = React.useMemo(
    () =>
      ADS_COMBINED_CONFIG.reduce((acc, cfg) => {
        const unit =
          cfg.unitType === UNIT_TYPE_CURRENCY
            ? getCurrencySymbol(currency)
            : cfg.unitType === UNIT_TYPE_PERCENT
            ? '%'
            : '';
        if (unit) acc[cfg.key] = unit;
        return acc;
      }, {} as Record<string, string>),
    [currency]
  );

  const { totalMetrics, averageMetrics, campaignCount } = React.useMemo(() => {
    try {
      return summarizeCampaignInsights(campaigns, insightConfig);
    } catch (e) {
      console.error('summarizeCampaignInsights failed', e);
      return {
        totalMetrics: {},
        averageMetrics: {},
        campaignCount: campaigns.length
      };
    }
  }, [campaigns, insightConfig]);

  const analyticsStats = React.useMemo(() => {
    try {
      return summarizeCampaignInsights(campaigns, statsConfig);
    } catch (e) {
      console.error('summarizeCampaignInsights (stats) failed', e);
      return { totalMetrics: {}, averageMetrics: {}, campaignCount: 0 };
    }
  }, [campaigns, statsConfig]);

  const bestPerformerCards = React.useMemo(() => {
    if (loading || error) return [];

    try {
      return campaigns?.length
        ? buildBestPerformerCards(campaigns, level, allConfig, currency)
        : [];
    } catch (e) {
      console.error('buildBestPerformerCards failed', e);
      return [];
    }
  }, [campaigns, level, allConfig, loading, error, currency]);

  const totalsEntries = React.useMemo(
    () => buildGrowthCardEntries(totalMetrics, insightConfig, t, currency),
    [totalMetrics, insightConfig, t, currency]
  );
  const averageEntries = React.useMemo(
    () => buildGrowthCardEntries(averageMetrics, insightConfig, t, currency),
    [averageMetrics, insightConfig, t, currency]
  );
  const statsEntries = React.useMemo(
    () =>
      buildGrowthCardEntries(
        analyticsStats.averageMetrics,
        statsConfig,
        t,
        currency
      ),
    [analyticsStats.averageMetrics, statsConfig, t, currency]
  );

  const sortOptions = React.useMemo(
    () =>
      allConfig.map((cfg) => ({
        name: cfg.key,
        title: t(`adsAnalytics.${cfg.key}`, cfg.title)
      })),
    [allConfig, t]
  );

  const navBarHeight =
    (document.getElementById('kt_app_header')?.clientHeight ?? 0) + 15 * 2 + 50;
  // --- UI states ---
  if (loading) {
    return (
      <div
        className="p-3 w-100 d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: 240 }}
      >
        <Spinner animation="border" role="status" />
        <div className="mt-2 small text-muted">
          {t('lomavisAdAnalytics.loading', 'Loading paid analytics…')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`
        }}
        className="w-100"
      >
        <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
          <Alert variant="danger" className="mb-3">
            <div className="fs-3">
              {t(
                'lomavisAdAnalytics.loadErrorTitle',
                "Couldn't load paid analytics"
              )}
            </div>
            <div className="small">{error}</div>
          </Alert>
          <Button variant="outline-primary" onClick={onRetry}>
            {t('lomavisAdAnalytics.retry', 'Retry')}
          </Button>
        </div>
      </Container>
    );
  }

  const isEmpty = campaigns.length === 0;
  if (isEmpty) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`
        }}
        className="w-100"
      >
        <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
          <span className="fs-3">
            {t('lomavisAdAnalytics.emptyTitle', 'No paid analytics yet')}
          </span>
          <Button
            className="mt-3"
            variant="outline-secondary"
            onClick={onRetry}
          >
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="p-3 w-100 d-flex flex-column gap-6">
      <div className="d-flex flex-column gap-6">
        <LomavisGrowthCardSectionTemplate
          title={t('adsAnalytics.overview.total')}
          entries={totalsEntries}
        />

        {campaignCount > 0 && (
          <LomavisGrowthCardSectionTemplate
            title={t('adsAnalytics.overview.average')}
            entries={averageEntries}
          />
        )}

        {campaignCount > 0 && (
          <LomavisGrowthCardSectionTemplate
            title={t('adsAnalytics.overview.stats')}
            entries={statsEntries}
          />
        )}

        {bestPerformerCards.length > 0 && campaignCount > 0 && (
          <div className="d-flex flex-column gap-3">
            <LomavisBestPerformingAds
              title={t('adsAnalytics.bestPerformers.list', 'Top performers')}
              itemsToShow={3}
              data={bestPerformerCards}
              sortOptions={sortOptions}
              customFilterOptions={[
                {
                  value: adLevel.ADS,
                  label: t('adsAnalytics.level.ads', 'Ads')
                },
                {
                  value: adLevel.ADSET,
                  label: t('adsAnalytics.level.adsets', 'Adsets')
                },
                {
                  value: adLevel.CAMPAIGNS,
                  label: t('adsAnalytics.level.campaigns', 'Campaigns')
                }
              ]}
              selectCustomFilter={(val) => setLevel(val as EntityLevel)}
              currentCustomFilter={level}
            />
          </div>
        )}
      </div>

      <LomavisCard1
        title={'lomavisAdAnalytics.adCampaigns'}
        showMoreInfo={false}
        showSheetDownloadButton={true}
        excelData={excelSheets}
        enablePreview
      >
        <LomavisPaidAnalyticsView1 data={data} metricUnits={metricUnits} />
      </LomavisCard1>
    </div>
  );
};

export default AdCampaigns;
